import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, LogOut, User, Search, Clock, AlertTriangle, Menu } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/contexts/AuthContext';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';
import { useToast } from '@/components/ui/use-toast';

interface HeaderProps {
  onMenuToggle?: () => void;
}

export function Header({ onMenuToggle }: HeaderProps) {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');

  const handleInactivityTimeout = useCallback(() => {
    toast({
      title: 'Sitzung abgelaufen',
      description: 'Sie wurden aufgrund von Inaktivität automatisch abgemeldet.',
      variant: 'destructive',
    });
    logout();
  }, [logout, toast]);

  const { remainingTimeFormatted, isWarning } = useInactivityTimer({
    onTimeout: handleInactivityTimeout,
    enabled: isAuthenticated,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <header className="flex h-16 items-center justify-between gap-2 border-b bg-card px-3 sm:px-6">
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuToggle}
        title="Navigation öffnen"
      >
        <Menu className="h-5 w-5" />
      </Button>

      {/* Search */}
      <form onSubmit={handleSearch} className="hidden max-w-md flex-1 items-center gap-2 sm:flex">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Suchen... (Enter zum Suchen)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="h-9 border-none bg-muted/50 focus-visible:ring-0"
        />
      </form>

      {/* Right side */}
      <div className="flex items-center gap-2 sm:gap-4">
        {/* Inactivity Timer */}
        {isAuthenticated && (
          <div 
            className={`hidden items-center gap-2 rounded-md px-3 py-1.5 font-mono text-sm md:flex ${
              isWarning 
                ? 'bg-red-100 text-red-700 border border-red-300 animate-pulse' 
                : 'bg-muted text-muted-foreground'
            }`}
            title="Verbleibende Sitzungszeit bis zur automatischen Abmeldung"
          >
            {isWarning ? (
              <AlertTriangle className="h-4 w-4" />
            ) : (
              <Clock className="h-4 w-4" />
            )}
            <span>{remainingTimeFormatted}</span>
          </div>
        )}

        {/* Notifications */}
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5" />
          <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive" />
        </Button>

        {/* User menu */}
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <User className="h-4 w-4" />
          </div>
          <div className="hidden sm:block">
            <p className="text-sm font-medium">{user?.displayName || user?.username}</p>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        {/* Logout */}
        <Button variant="ghost" size="icon" onClick={logout} title="Abmelden">
          <LogOut className="h-5 w-5" />
        </Button>
      </div>
    </header>
  );
}
